import React, { useEffect, useState } from 'react';
import { useFormSubmissionsClient } from '../submissions/formSubmissions.client';
import { useOrganization } from '@clerk/clerk-react';
import { TimeSelector } from '../components';
import MetricsRow from './MetricsRow';
import SubmissionByInterface from './SubmissionByInterface';
import CompletionRate from './CompletionRate';
import MultiModalInputs from './MultiModalInputs';
import RecentSubmissions from './RecentSubmissions';
import { PageLoader } from '../components/PageLoader';
import { PageError } from '../components/PageError';
export const DashboardPage = () => {
    const submissionsClient = useFormSubmissionsClient();
    const { organization, memberships, isLoaded: orgLoaded } = useOrganization({
        memberships: {
            infinite: true,
        },
    });
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDays, setSelectedDays] = useState(30);
    // Color palette matching the design
    const BRAND_COLORS = {
        primary: '#F97316',
        secondary: '#6366F1',
        tertiary: '#10B981',
        quaternary: '#8B5CF6',
        gray: '#94A3B8',
        lightGray: '#F1F5F9'
    };
    // Get real completion rate data from API or fallback to empty array
    const completionRateData = dashboardData?.weeklyCompletionRates || [];
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await submissionsClient.getDashboardStats(selectedDays);
                setDashboardData(data);
            }
            catch (err) {
                setError('Failed to load dashboard data. Please try again.');
            }
            finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, [selectedDays]);
    const handleTimeFilterChange = (value) => {
        setSelectedDays(value);
    };
    // Add colors to interface data
    const interfaceDataWithColors = dashboardData?.submissionsByInterface?.map((item, index) => ({
        ...item,
        color: [BRAND_COLORS.secondary, BRAND_COLORS.primary, BRAND_COLORS.tertiary, BRAND_COLORS.quaternary][index % 4]
    })) || [];
    // Add colors to modal input data
    const submissionsByModeWithColors = dashboardData?.submissionsByMode?.map((item, index) => ({
        ...item,
        color: [BRAND_COLORS.secondary, BRAND_COLORS.primary, BRAND_COLORS.tertiary, BRAND_COLORS.quaternary, BRAND_COLORS.gray][index % 5]
    })) || [];
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return React.createElement("div", { className: "bg-white p-3 border border-gray-200 shadow-md rounded-md" },
                React.createElement("p", { className: "font-medium" }, label),
                payload.map((entry, index) => React.createElement("p", { key: `item-${index}`, style: {
                        color: entry.color || entry.fill
                    }, className: "text-sm" }, `${entry.name || entry.dataKey}: ${entry.value}${entry.dataKey === 'rate' ? '%' : ''}`)));
        }
        return null;
    };
    const formatCompletionTime = (timeMs) => {
        const totalSeconds = Math.floor(timeMs / 1000);
        const days = Math.floor(totalSeconds / (24 * 60 * 60));
        const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
        const seconds = totalSeconds % 60;
        const parts = [];
        if (days > 0)
            parts.push(`${days}d`);
        if (hours > 0)
            parts.push(`${hours}h`);
        if (minutes > 0)
            parts.push(`${minutes}m`);
        if (seconds > 0 || parts.length === 0)
            parts.push(`${seconds}s`);
        return parts.join(' ');
    };
    if (loading || !orgLoaded) {
        return React.createElement(PageLoader, { text: "Loading analytics..." });
    }
    if (error) {
        return (React.createElement(PageError, { title: "Error Loading Analytics", error: error }));
    }
    if (!dashboardData) {
        return (React.createElement("div", { className: "flex flex-col w-full min-h-screen" },
            React.createElement("div", { className: "flex-1 flex items-center justify-center bg-gray-50" },
                React.createElement("div", { className: "bg-gray-50 border border-gray-200 rounded-lg p-6 max-w-md" },
                    React.createElement("h2", { className: "text-lg font-semibold text-gray-800 mb-2" }, "No Data Available"),
                    React.createElement("p", { className: "text-gray-600" }, "Dashboard data is not available at the moment.")))));
    }
    return (React.createElement("div", { className: "flex flex-col w-full min-h-screen" },
        React.createElement("div", { className: "flex-1 p-6 bg-gray-50" },
            React.createElement("div", { className: "w-3/5 mx-auto mx-auto" },
                React.createElement("div", { className: "flex justify-between items-center mb-6" },
                    React.createElement("div", null,
                        React.createElement("h1", { className: "text-2xl font-bold text-gray-800" }, "Analytics"),
                        React.createElement("p", { className: "text-gray-600 mt-1" }, "Platform overview of forms, submissions, and analytics")),
                    React.createElement("div", { className: "flex items-center space-x-3" },
                        React.createElement(TimeSelector, { selectedDays: selectedDays, onChange: handleTimeFilterChange }))),
                React.createElement(MetricsRow, { dashboardData: dashboardData, formatCompletionTime: formatCompletionTime }),
                React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" },
                    React.createElement(SubmissionByInterface, { interfaceDataWithColors: interfaceDataWithColors, CustomTooltip: CustomTooltip }),
                    React.createElement(CompletionRate, { completionRateData: completionRateData, currentCompletionRate: dashboardData.completionRate, brandColors: BRAND_COLORS, CustomTooltip: CustomTooltip })),
                React.createElement(MultiModalInputs, { submissionsByMode: submissionsByModeWithColors, CustomTooltip: CustomTooltip }),
                React.createElement(RecentSubmissions, { latestSubmissions: dashboardData.latestSubmissions, totalSubmissions: dashboardData.totalSubmissions })))));
};
export default DashboardPage;
